import React from 'react';
import { HandIcon, XIcon } from 'lucide-react';
import { InitialAvatar } from '../InitialAvatar';
import { Button } from '../ui/Button';
import { BadgeMark } from '../profile/MemberBadges';
import { PrototypeNote } from '../prototype/PrototypeNote';
import { prototypeStateFor } from '../../data/prototypeStates';
import { AFFILIATION_LABELS, CURRENT_MEMBER, overlapWith, publicName } from '../../data/community';
import type { Member } from '../../data/community';
interface PersonDetailProps {
  member: Member | null;
  onClose: () => void;
  onOpenProfile: (member: Member) => void;
}
export function PersonDetail({
  member,
  onClose,
  onOpenProfile
}: PersonDetailProps) {
  if (!member) {
    return <div className="flex h-full flex-col justify-center gap-2 rounded-card border border-dashed border-rule p-6" data-id="element-277" data-mp="34ktdu8r6kayi">
        <p className="text-body text-ink" data-id="element-278" data-mp="16syv0isrsk6v">Pick anyone on the chart.</p>
        <p className="text-small text-ink-muted" data-id="element-279" data-mp="12s57vrn5j9f1">
          Bars that sit under your years belong to people who were here when you were.
        </p>
      </div>;
  }
  const isYou = member.name === CURRENT_MEMBER.fullName;
  const overlap = isYou ? null : overlapWith(member, CURRENT_MEMBER);
  const memorial = member.memorial;
  const note = prototypeStateFor(member.name);
  return <div className="flex h-full flex-col gap-5 rounded-card border border-rule bg-card p-5" data-id="element-280" data-mp="1jtqxvvrm6pop">
      <div className="flex items-start justify-between gap-3" data-id="element-281" data-mp="27jv2a5cmmtgr">
        <div className="flex items-center gap-3" data-id="element-282" data-mp="27kd5jlxpi7y6">
          <InitialAvatar name={member.name} size={48} data-id="element-283" data-mp="2cj7msjg0zog6" />
          <div className="flex flex-col" data-id="element-284" data-mp="2ckshc92cb7e1">
            <span className="flex items-center gap-1.5 text-heading font-semibold text-ink" data-id="element-285" data-mp="1yrt1g5ynbb5y">
              {isYou ? 'You' : publicName(member.name)}
              <BadgeMark name={member.name} data-id="element-286" data-mp="22r1ut9prx70z" />
            </span>
            <span className="text-small text-ink-muted" data-id="element-287" data-mp="2qhecvdeqgk5a">
              {member.years} · {AFFILIATION_LABELS[member.affiliation]}
            </span>
            {memorial ? <span className="mt-1 inline-flex w-fit items-center gap-1.5 text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-288" data-mp="355o6j3re0e90">
                <span aria-hidden="true" className="h-px w-4 bg-highlight" data-id="element-289" data-mp="3142m8xciwxos" />
                In memory
              </span> : null}
            {note ? <PrototypeNote className="mt-1.5 max-w-full" data-id="element-290" data-mp="2r5womrxr2dpq">{note.label}</PrototypeNote> : null}
          </div>
        </div>

        <button type="button" onClick={onClose} className="inline-flex h-11 w-11 items-center justify-center rounded-pill text-ink-muted hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-291" data-mp="2m7v19utajs8g">
          <span className="sr-only" data-id="element-292" data-mp="28gq7vaginw6k">Close</span>
          <XIcon aria-hidden="true" className="h-4 w-4" data-id="element-293" data-mp="23gc4i5pv1aev" />
        </button>
      </div>

      {overlap ? <p className="rounded-field bg-primary-soft px-4 py-3 text-small text-primary-text" data-id="element-294" data-mp="1ygfxdxlv92v3">
          You were both here from{' '}
          <span className="font-semibold" data-id="element-295" data-mp="1ocj10pdoqi7f">
            {overlap.start}–{overlap.end}
          </span>
          .
        </p> : !isYou ? <p className="rounded-field bg-canvas px-4 py-3 text-small text-ink-muted" data-id="element-296" data-mp="1yb8abtrimuc2">
          Your years don&apos;t overlap. They were here{' '}
          {member.years.replace('–', ' to ')}.
        </p> : null}

      <dl className="flex flex-col gap-3 text-small" data-id="element-297" data-mp="15n2ot4sn11b6">
        {member.city ? <div className="flex justify-between gap-4 border-b border-rule pb-3" data-id="element-298" data-mp="1akuq2pf69zs1">
            <dt className="text-ink-muted" data-id="element-299" data-mp="1fkh2wxr39e0b">Where now</dt>
            <dd className="text-ink" data-id="element-300" data-mp="1fiw8r98orx2z">{member.city}</dd>
          </div> : null}
        {member.note && !isYou ? <div className="flex justify-between gap-4 border-b border-rule pb-3" data-id="element-301" data-mp="1kda97q27guei">
            <dt className="text-ink-muted" data-id="element-302" data-mp="mva2tubzkgcy">Known for</dt>
            <dd className="text-right text-ink" data-id="element-303" data-mp="rucxqly95677">{member.note}</dd>
          </div> : null}
        <div className="flex justify-between gap-4" data-id="element-304" data-mp="2tjix7onpoud9">
          <dt className="text-ink-muted" data-id="element-305" data-mp="2tkbxc6s60lmf">Contact</dt>
          <dd className="text-right text-ink-muted" data-id="element-306" data-mp="2olqstv5vrfs6">Never shown to members</dd>
        </div>
      </dl>

      {note ? <PrototypeNote variant="block" data-id="element-307" data-mp="33awzu5aymaam">{note.note}</PrototypeNote> : null}

      {memorial ? <div className="mt-auto flex flex-col gap-3" data-id="element-308" data-mp="2yi3xfo2yre1p">
          <p className="rounded-field bg-canvas px-4 py-3 text-small text-ink-muted" data-id="element-309" data-mp="2yhbz45hzf27q">
            {publicName(member.name)} died in {memorial.died}. Their page is a memorial. You can
            read it and add something you remember.
          </p>
          <Button variant="secondary" onClick={() => onOpenProfile(member)} data-id="element-310" data-mp="3m7ohkaaushck">
            Open their memorial
          </Button>
        </div> : !isYou ? <div className="mt-auto flex flex-wrap gap-2" data-id="element-311" data-mp="1rugho480tfk2">
          <Button data-id="element-312" data-mp="1rv8bpalyz588">
            <HandIcon aria-hidden="true" className="h-4 w-4" data-id="element-313" data-mp="1rwt00ihoxrxn" />
            Wave
          </Button>
          <Button variant="secondary" onClick={() => onOpenProfile(member)} data-id="element-314" data-mp="1mxq5hrzc743x">
            See profile
          </Button>
        </div> : <div className="mt-auto" data-id="element-315" data-mp="7cxj6rel42tv">
          <Button variant="secondary" onClick={() => onOpenProfile(member)} data-id="element-316" data-mp="7c4hw5yea5j4">
            Your profile
          </Button>
        </div>}
    </div>;
}